import { type ReactElement, type ReactNode, useMemo, useState } from "react";

import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import {
  BooksIcon,
  GaugeIcon,
  GraduationCapIcon,
  HeadphonesIcon,
  HeartIcon,
  TargetIcon,
  TranslateIcon,
  UserListIcon,
} from "@phosphor-icons/react/dist/ssr";

import { TabHeader } from "@/components/Luna/features";
import useLesson from "@/contexts/useLesson";
import { type LearningProfile } from "@/types";

import { getLearningPaceLabel, getLearningStyleLabel } from "./utils";

type ProfileSection = "basic" | "learning";

const LearningPreferencesIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M2 4H12"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
    <path
      d="M2 10H12"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
    <circle cx="5.25" cy="4" r="1.5" fill="currentColor" />
    <circle cx="8.75" cy="10" r="1.5" fill="currentColor" />
  </svg>
);

interface ProfileItem {
  label: string;
  value: string | string[];
}

interface ProfileCard {
  title: string;
  icon: ReactNode;
  items: ProfileItem[];
}

const sectionTabs: {
  label: string;
  value: ProfileSection;
  icon: ReactElement;
}[] = [
  {
    label: "Basic Information",
    value: "basic",
    icon: <UserListIcon size={14} weight="fill" />,
  },
  {
    label: "Learning Preferences",
    value: "learning",
    icon: <LearningPreferencesIcon />,
  },
];

const hasValue = (value?: string | string[]) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return Boolean(value?.trim());
};

const formatSelection = (value: string) =>
  value
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (character: string) => character.toUpperCase());

const getLearningPreferenceCards = (
  profile?: LearningProfile
): ProfileCard[] => {
  if (!profile) {
    return [];
  }

  const subjects = profile.subjects?.map((subject) => subject.name) ?? [];
  const specialties =
    profile.subjects?.flatMap((subject) => subject.specialties ?? []) ?? [];

  return [
    {
      title: "Language Preferences",
      icon: <TranslateIcon size={16} weight="fill" />,
      items: [
        { label: "Primary Language", value: profile.preferredLanguage },
        { label: "Additional Languages", value: profile.secondaryLanguages },
      ],
    },
    {
      title: "Subjects & Specialties",
      icon: <BooksIcon size={16} weight="fill" />,
      items: [
        { label: "Subjects", value: subjects },
        { label: "Specialties", value: specialties },
      ],
    },
    {
      title: "Learning Goals",
      icon: <TargetIcon size={16} weight="fill" />,
      items: [
        { label: "Short-Term Goals", value: profile.goals },
        { label: "Long-Term Goals", value: profile.longTermGoals },
      ],
    },
    {
      title: "Learning Style",
      icon: <HeadphonesIcon size={16} weight="fill" />,
      items: [
        {
          label: "Primary Learning Style",
          value: profile.learningStyle
            ? getLearningStyleLabel(profile.learningStyle)
            : "",
        },
        {
          label: "Preferred Content Format",
          value: profile.contentFormats?.map(formatSelection),
        },
      ],
    },
    {
      title: "Motivation & Confidence",
      icon: <HeartIcon size={16} weight="fill" />,
      items: [
        { label: "What Motivates You", value: profile.motivationFactors },
        { label: "Academic Confidence Level", value: profile.confidenceLevel },
      ],
    },
    {
      title: "Learning Pace",
      icon: <GaugeIcon size={16} weight="fill" />,
      items: [
        {
          label: "Preferred Pace of Learning",
          value: profile.pace ? getLearningPaceLabel(profile.pace) : "",
        },
      ],
    },
    {
      title: "Study Habits",
      icon: <GraduationCapIcon size={16} weight="fill" />,
      items: [{ label: "", value: profile.studyHabits }],
    },
  ].map((card) => ({
    ...card,
    items: card.items.filter((item) => hasValue(item.value)),
  }));
};

const getBasicInformationCards = (profile?: LearningProfile): ProfileCard[] => {
  if (!profile) {
    return [];
  }

  const dateOfBirth = profile.dateOfBirth?.toDate?.() ?? profile.dateOfBirth;
  const personalItems: ProfileItem[] = [
    { label: "Preferred Name", value: profile.preferredName },
    { label: "Gender", value: profile.gender },
  ];

  if (dateOfBirth instanceof Date) {
    personalItems.push({
      label: "Date of Birth",
      value: dateOfBirth.toLocaleDateString(),
    });
  }

  return [
    {
      title: "Personal Information",
      icon: <UserListIcon size={16} weight="fill" />,
      items: personalItems,
    },
    {
      title: "Academic Information",
      icon: <GraduationCapIcon size={16} weight="fill" />,
      items: [
        { label: "Grade Level", value: profile.gradeLevel },
        { label: "School", value: profile.schoolName },
        { label: "Program/Major", value: profile.programMajor },
        { label: "Curriculum Region", value: profile.curriculumRegion },
      ],
    },
    {
      title: "Interests & Study Breaks",
      icon: <HeartIcon size={16} weight="fill" />,
      items: [
        { label: "Interests", value: profile.interests },
        { label: "Ideal Break", value: profile.idealBreak },
      ],
    },
  ].map((card) => ({
    ...card,
    items: card.items.filter((item) => hasValue(item.value)),
  }));
};

const renderValue = (value: string | string[]) => {
  if (Array.isArray(value)) {
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
        {value.map((item, index) => (
          <Chip
            key={`${item}-${index}`}
            label={item}
            size="small"
            sx={{
              backgroundColor: "background.paper",
              color: "text.primary",
              fontSize: 12,
              lineHeight: "18px",
              minHeight: 24,
            }}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Typography variant="text13R" color="text.primary">
      {value}
    </Typography>
  );
};

const EmptyState = ({ label }: { label: string }) => (
  <Card
    variant="outlined"
    sx={{
      borderColor: "border.main",
      backgroundColor: "transparent",
      p: 0,
    }}
  >
    <CardContent sx={{ py: 4, textAlign: "center" }}>
      <Typography variant="text13R" color="text.secondary">
        No {label.toLowerCase()} available for this student.
      </Typography>
    </CardContent>
  </Card>
);

const ProfileCardGrid = ({
  cards,
  emptyLabel,
}: {
  cards: ProfileCard[];
  emptyLabel: string;
}) => {
  const visibleCards = cards.filter((card) => card.items.length > 0);

  if (visibleCards.length === 0) {
    return <EmptyState label={emptyLabel} />;
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: 1.5,
        width: "100%",
      }}
    >
      {visibleCards.map((card) => (
        <Card
          key={card.title}
          variant="outlined"
          sx={{
            minHeight: 132,
            borderColor: "border.main",
            borderRadius: 1,
            backgroundColor: "transparent",
            p: 0,
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Box
                  sx={{
                    display: "grid",
                    placeItems: "center",
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    color: "text.primary",
                    backgroundColor: "background.paper",
                  }}
                >
                  {card.icon}
                </Box>
                <Typography variant="text15B">{card.title}</Typography>
              </Stack>
              <Stack spacing={1.25}>
                {card.items.map((item) => (
                  <Box key={item.label || card.title}>
                    {item.label && (
                      <Typography
                        variant="text12L"
                        color="text.secondary"
                        sx={{ mb: 0.25 }}
                      >
                        {item.label}
                      </Typography>
                    )}
                    {renderValue(item.value)}
                  </Box>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

const StudentProfile = () => {
  const { student } = useLesson();
  const [activeSection, setActiveSection] =
    useState<ProfileSection>("learning");

  const basicInformationCards = useMemo(
    () => getBasicInformationCards(student?.learningProfile),
    [student?.learningProfile]
  );
  const learningPreferenceCards = useMemo(
    () => getLearningPreferenceCards(student?.learningProfile),
    [student?.learningProfile]
  );

  return (
    <Box
      width="100%"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TabHeader
        title="Student Profile"
        icon={<UserListIcon size={16} weight="fill" />}
      />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          minHeight: 0,
          pb: 2,
        }}
      >
        <Tabs
          value={activeSection}
          onChange={(_, value: ProfileSection) => setActiveSection(value)}
          sx={{ mb: 2, flexShrink: 0 }}
        >
          {sectionTabs.map((section) => (
            <Tab
              key={section.value}
              value={section.value}
              icon={section.icon}
              iconPosition="start"
              label={section.label}
            />
          ))}
        </Tabs>
        {activeSection === "basic" && (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ProfileCardGrid
              cards={basicInformationCards}
              emptyLabel="basic information"
            />
          </Box>
        )}
        {activeSection === "learning" && (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ProfileCardGrid
              cards={learningPreferenceCards}
              emptyLabel="learning preferences"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StudentProfile;
